import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

/**
 * ForgotPasswordPage — public broker password-reset request.
 *
 * The API returns the same generic 200 body regardless of whether the
 * email belongs to a broker, a Google-only account, an admin, or is
 * unknown (anti-enumeration). Once the form submits successfully we
 * always show the same confirmation copy — never leaking which branch
 * the backend hit.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-reset", {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
    } catch (err) {
      // 429 (IP-rate-limit) is the only surface-able failure — every
      // other branch returns 200. Show a friendly hint and let the
      // user retry after a short cooldown.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 || detail === "RATE_LIMITED") {
        toast.error("Too many attempts. Please try again in a bit.");
      } else {
        toast.error(formatApiError(err, "Request failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16">
      <div
        className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-10 shadow-mb-card mb-rise"
        data-testid="forgot-password-page"
      >
        <div className="mb-8 flex items-center">
          <Link
            to="/login"
            className="mb-focus-ring inline-flex items-center rounded-lg"
          >
            <BrandLogo className="h-9" />
          </Link>
        </div>

        <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
          <KeyRound className="h-3 w-3" />
          Password recovery
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Check your inbox.
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-gray-500">
              If a MatchBroker account exists for that email, we&apos;ve just
              sent instructions to reset the password. The link expires in{" "}
              <span className="font-semibold text-gray-700">30 minutes</span>.
            </p>
            <p className="mt-3 text-xs text-gray-400">
              Google-linked accounts will receive a note reminding them to
              sign in with Google instead.
            </p>
            <div className="mt-8">
              <Link to="/login" data-testid="forgot-password-back-to-login">
                <Button className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white hover:bg-[#14213A]">
                  Back to sign in
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-6"
            data-testid="forgot-password-form"
          >
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                Forgot your password?
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                Enter your account email and we&apos;ll send you a secure link
                to set a new password.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fp-email" className="text-gray-700">
                Email
              </Label>
              <Input
                id="fp-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@matchbroker.ph"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="forgot-password-email"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white hover:bg-[#14213A] disabled:opacity-70"
              data-testid="forgot-password-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending link…
                </>
              ) : (
                "Send reset link"
              )}
            </Button>

            <p className="pt-2 text-center text-sm text-gray-500">
              Remembered it?{" "}
              <Link
                to="/login"
                className="font-semibold text-[#1B2B4B] hover:underline"
                data-testid="forgot-password-goto-login"
              >
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
